import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Truck,
  DollarSign,
  Save,
  RefreshCw,
  Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function DeliverySettings() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    rate_per_mile: 0.50,
    minimum_delivery_fee: 2.50,
    maximum_delivery_fee: 15.00,
    free_delivery_threshold: 30.00
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/delivery-rate');
      setSettings({
        rate_per_mile: response.data.rate_per_mile || 0.50,
        minimum_delivery_fee: response.data.minimum_delivery_fee || 2.50,
        maximum_delivery_fee: response.data.maximum_delivery_fee || 15.00,
        free_delivery_threshold: response.data.free_delivery_threshold || 30.00
      });
    } catch (error) {
      toast.error('Failed to fetch delivery settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/delivery-rate', settings);
      toast.success('Delivery settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Calculate sample fees for preview
  const sampleDistances = [1, 3, 5, 10];
  const calculateSampleFee = (distance) => {
    const calculated = distance * settings.rate_per_mile;
    return Math.max(settings.minimum_delivery_fee, Math.min(calculated, settings.maximum_delivery_fee));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Delivery Rate Settings</h1>
        <p className="text-slate-500">Configure delivery fees charged to customers</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings Form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-500" />
            Rate Configuration
          </h2>

          <div className="space-y-5">
            <div>
              <Label className="mb-2 block">Rate Per Mile (£)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">£</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.rate_per_mile}
                  onChange={(e) => handleInputChange('rate_per_mile', e.target.value)}
                  className="pl-8"
                  data-testid="rate-per-mile-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">UK average: £0.45-0.55 per mile</p>
            </div>

            <div>
              <Label className="mb-2 block">Minimum Delivery Fee (£)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">£</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.minimum_delivery_fee}
                  onChange={(e) => handleInputChange('minimum_delivery_fee', e.target.value)}
                  className="pl-8"
                  data-testid="min-fee-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Charged even for very short distances</p>
            </div>

            <div>
              <Label className="mb-2 block">Maximum Delivery Fee (£)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">£</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.maximum_delivery_fee}
                  onChange={(e) => handleInputChange('maximum_delivery_fee', e.target.value)}
                  className="pl-8"
                  data-testid="max-fee-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Cap for long-distance deliveries</p>
            </div>

            <div>
              <Label className="mb-2 block">Free Delivery Threshold (£)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">£</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.free_delivery_threshold}
                  onChange={(e) => handleInputChange('free_delivery_threshold', e.target.value)}
                  className="pl-8"
                  data-testid="free-threshold-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Orders above this get free delivery</p>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full btn-primary mt-4"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Fee Preview
            </h2>

            <div className="space-y-3">
              {sampleDistances.map((distance) => (
                <div key={distance} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-slate-600">{distance} mile{distance > 1 ? 's' : ''}</span>
                  <span className="font-semibold text-slate-900">
                    £{calculateSampleFee(distance).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-4">
            <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" />
              How it works
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Delivery fee = Distance × Rate per mile</li>
              <li>• Fee never drops below minimum</li>
              <li>• Fee never exceeds maximum</li>
              <li>• Orders over £{settings.free_delivery_threshold.toFixed(2)} = FREE delivery</li>
            </ul>
          </div>

          <div className="bg-orange-50 rounded-xl p-4">
            <h3 className="font-medium text-orange-800 mb-2">Driver Earnings</h3>
            <p className="text-sm text-orange-700">
              Drivers receive the delivery fee as their payment for each delivery. 
              Setting competitive rates helps attract and retain quality drivers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
